import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  TextField,
  InputAdornment,
  IconButton,
  Toolbar,
  Typography,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  LightMode,
  DarkMode,
  Menu,
} from '@mui/icons-material';
import PatientListDialog from "../popup/PatientListDialog";
import { cleanHTML } from "../lib/helper";
import Dashboard from "./Dashboard";
import { useLocation, useNavigate } from "react-router-dom";
import FloatingMenu from "../common/FloatingMenu";
import { ReactComponent as InactiveMic } from "../assets/mic_inactive.svg";
import { ReactComponent as ActiveMic } from "../assets/microphone 1.svg";
import { ReactComponent as HomeInactive } from "../assets/home.svg";
import { ReactComponent as HomeActive } from "../assets/Home_fill.svg";
import { ReactComponent as Logo } from "../assets/objects.svg";
import {
  getNameInitials,
  getTimeAgo,
  getUserLocalData,
  removeExistingSessionData,
  saveRecentChatPatientData,
} from "../lib/helper";
const drawerWidth = 80;

const SidebarIcon = ({ icon, tooltip, onClick }) => (
  <Tooltip title={tooltip} placement="right">
    <IconButton sx={{ color: '#fff', my: 1 }} onClick={onClick}>{icon}</IconButton>
  </Tooltip>
);

const DashboardLayout = ({ children, active }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [recentChatList, setRecentChatList] = useState([]);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navigate = useNavigate();
  const location = useLocation();
  const chatsMessages = location?.state?.chatsMessages || [];
  const token = JSON.parse(localStorage.getItem("access_token"));
  const patient_id = localStorage.getItem("patient_id");
  const session_id = localStorage.getItem("session_id");
  const [query, setQuery] = useState("");
  const [messages, setMessages] = useState(chatsMessages);
  const [patientList, setPatientList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState("");
  const { user_name } = getUserLocalData();

  useEffect(() => {
    getRecentChat();
  }, []);

  //recent chats api
  const getRecentChat = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_QUERY_URL}/conversation-list`
      );

      if (response?.data) {
        const filteredData = response?.data?.filter(
          (msg) => msg.message_count > 2
        );
        setRecentChatList(filteredData);
      }
    } catch (error) {
      console.error("Error:", error?.response?.data?.detail);
    }
  };

  const clearMessages = () => {
    setMessages([]); // Reset chat messages
  };
  const drawerContent = (
    <Box
      sx={{
        bgcolor: '#111827',
        height: '100%',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        py: 2,
        alignItems: 'center',
      }}
    >
      {/* Top: Logo + Nav Icons */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Logo />

        <SidebarIcon onClick={() => {
          setMobileOpen(false);
          navigate("/chat", { state: { conversationId: null } });
        }} icon={<HomeActive />} tooltip="Home" />
        <SidebarIcon onClick={() => {
          navigate("/voice", { state: "search" });
          setMobileOpen(false);
          getRecentChat();
        }} icon={<ActiveMic />} tooltip="Voice" />
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#0e1629' }} component={'div'} className="layout-container">
      <div class="ellipse-left"></div>
      <div class="ellipse-right"></div>
      <CssBaseline />

      {/* Sidebar for mobile and desktop */}
      <Box
        component="nav"
        sx={{
          width: { sm: drawerWidth },
          flexShrink: { sm: 0 },
        }}
      >
        {/* Mobile temporary drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
            },
          }}
        >
          {drawerContent}
        </Drawer>

        {/* Desktop permanent drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          open
        >
          {drawerContent}
        </Drawer>
      </Box>

      {/* Main Area */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            height: '60px',
            bgcolor: '#0e1629',
            borderBottom: '1px solid #1f2937',
            zIndex: '2',
            px: 2,
            display: 'flex',
            justifyContent: 'space-between',
            width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
            ml: { xs: 0, sm: `${drawerWidth}px` },
          }}
        >
          <Toolbar sx={{ height: '100%', px: 0, width: '100%' }}>
            {isMobile && (
              <IconButton
                color="inherit"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2 }}
              >
                <Menu />
              </IconButton>
            )}
            <Box>
              <Typography className='heading'>Pulse IQ</Typography>
            </Box>
            <Box sx={{ flexGrow: 1 }} />

            <Box>
              <IconButton sx={{ color: '#fff' }}>
                <LightMode fontSize="small" />
              </IconButton>
              <IconButton sx={{ color: '#00f5c4' }}>
                <DarkMode fontSize="small" />
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>


        {/* Main Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 2,
            pt: '70px', // space for fixed header
            width: { xs: '100%' },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            height: '100%',
          }}
        >

          {children}

        </Box>

      </Box>
      {open && (
        <PatientListDialog
          open={open}
          setOpen={setOpen}
          data={patientList}
          message={responseMessage}
          clearMessages={clearMessages}
        />
      )}
    </Box>
  );
}

export default DashboardLayout;